/**
 *   Screen 1 → Gather Zip Code + Check Service Area + Reserve Slot
 *   Screen 2 → Identify Client + Check Household for Existing Appointment or Pledges
 *   Screen 3 → Gather Household Information
 *   Screen 4 → Show Income Guidelines for the Household Size entered (display only)
 *   Screen 5 → Slot + Staff Assignment
 *   Screen 6 → Confirmation
 */
export type Step = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * The terminal type for a booking refused because public booking is closed: before the booking
 * season, after it, or with none set. Its detail carries the season's website message as `message`
 * and nothing else — the public sees one message whatever the reason.
 */
export const BOOKING_SEASON_CLOSED_TERMINAL = "booking_season_closed";

/**
 * The terminal type for a booking CMS refused because no funding program remains for the household:
 * it has already received the program this year (409 code programAlreadyReceived), or the program is
 * switched off (409 code programDisabled). Its detail carries the server's `error` as `message`.
 * Nothing was booked, and neither another try nor another time would change the answer, so the
 * screen offers only Start Over.
 */
export const PROGRAM_REFUSED_TERMINAL = "program_refused";

/** The 409 `code`s that land on {@link PROGRAM_REFUSED_TERMINAL}. */
export const PROGRAM_REFUSED_CODES: readonly string[] = ["programAlreadyReceived", "programDisabled"];

/**
 * The terminal type for a booking that could not be made because the time held for it is gone: the
 * hold expired or was released (ReservationNotFound), the time was booked by somebody else
 * (AlreadyBooked), or the site's set-up changed under it. Nothing was booked, and resubmitting the
 * same hold can only fail again, so the screen offers "Find another time" — a new hold for the saved
 * ZIP, then the same answers resubmitted against it — and never "Try Again". See
 * createBookingFailure.ts for which server answers land here. No detail.
 */
export const HELD_TIME_GONE_TERMINAL = "held_time_gone";

/** The terminal type for the 409 slotUnavailable answer. Offers the same "Find another time". */
export const SLOT_TAKEN_TERMINAL = "slot_taken";

export interface BookingCustomer {
  firstName: string;
  lastName: string;
  /**
   * Nine digits, optional, and it stays optional: a household where nobody has a Social Security
   * number is a household this agency serves, and the form says so. Sent to the server, which
   * encrypts it — see buildCreateBookingPayload.
   */
  ssn?: string;
  /**
   * ISO yyyy-MM-dd. Optional. It reaches 99.99 per cent of the caseload where a real Social Security
   * number reaches 37 per cent, and it is what turns a name from a 4.4 per cent collision into a
   * 0.045 per cent one.
   */
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  smsOptIn?: boolean;
  energyAccountNumber?: string;
  hhId?: string;
  personId?: string;
}

export interface BookingAttendee {
  name: string;
  email: string;
  phoneNumber: string;
  timeZone: string;
  /** ISO yyyy-MM-dd, optional. Matches CreateBookingAttendee.DateOfBirth on the API. */
  dateOfBirth?: string;
  /**
   * Nine digits, optional. Matches CreateBookingAttendee.Ssn on the API, which normalizes it, mints a
   * keyed token from it and stores the digits encrypted. Never comes back on a read: the booking
   * responses this type also describes never carry one.
   */
  ssn?: string;
  /**
   * Whether the applicant ticked the box agreeing to appointment texts. Matches
   * CreateBookingAttendee.SmsOptIn on the API, which records it on the booking request and uses it to
   * decide whether a confirmation text is sent. Absent means no.
   */
  smsOptIn?: boolean;
  /**
   * The utility account number, as typed. Optional and unvalidated — providers number accounts
   * differently, so a format check here would reject real ones. Matches
   * CreateBookingAttendee.EnergyAccountNumber on the API, which stores it on the booking request for
   * staff to read.
   */
  energyAccountNumber?: string;
}

export interface BookingHost {
  id: number;
  name: string;
}

export interface Booking {
  id: number;
  uid: string;
  title: string;
  status: string;
  start: string;
  end: string;
  location?: string;
  eventType?: { id: number; slug: CalEventTypeSlug };
  attendees?: BookingAttendee[];
  hosts?: BookingHost[];
  householdId?: string;
}

export interface Household {
  householdSize: number; // Must be between 1 and 24
  // Total monthly income from everyone in the household. Absent while AmsBookingWithoutIncome is on
  // (AB#840): the household step no longer asks, and the request omits it.
  householdIncome?: number;
  address: string;
  energySource: "PSE" | "Other Heating and Energy Source";
  pseService?: "Gas" | "Electric" | "Both gas and electric";
}

export interface ProfileCheckResult {
  profileFound: boolean;
  householdFound: boolean;
  personId: string;
  householdId: string;
}

/**
 * What `POST /api/phone/verify` actually returns. `valid` means the number is well-formed and in a
 * range a carrier can assign — not that the line exists or can receive a text, which are billable
 * Twilio Lookup add-ons CMS does not request. (`lineType` and `carrierName` used to be declared here
 * and were never sent by the server; they would come from those add-ons.)
 */
export interface PhoneLookupResult {
  valid: boolean;
  /** E.164, as Twilio resolved it. */
  phoneNumber?: string;
  nationalFormat?: string;
  message?: string;
  /** ISO 3166-1 alpha-2, e.g. "US". */
  countryCode?: string;
  /** International dialing prefix without the "+", e.g. "1". */
  callingCountryCode?: string;
}

export interface MscSite {
  name: string;
  address: string;
  phone: string;
  /** Cal team slug for the site, e.g. "federal-way-main". Null until the team exists in Cal. */
  calTeamSlug: string | null;
  /** Which event type the site publishes for booking. Null alongside calTeamSlug. */
  calEventTypeSlug: CalEventTypeSlug | null;
}

export interface ServicingMscSite extends MscSite {
  preapproved: boolean
}

export interface ServiceAreaResult {
  mscSites: ServicingMscSite[];
  servicedBy?: string;
  servicerPhone?: string;
  servicerPhoneExtension?: string;
  servicerWebsiteLink?: string;
  message?: string;
}

/** Cal event type slugs the booking flow can ask for; the public flow only ever books "intake". */
export type CalEventTypeSlug = "intake" | "off-slot";

export const INTAKE_EVENT_TYPE_SLUG: CalEventTypeSlug = "intake";

export interface SlotReservation {
  site: ServicingMscSite;
  /** The team the hold is against — resolved and non-null, unlike the site's own slug. */
  teamSlug: string;
  /** Which of the site's event types is being held — see PublicSlotReservation. */
  eventTypeSlug: CalEventTypeSlug;
  slotStart: string;
  reservationUid: string;
  reservationUntil?: string;
}

// Matches AmsSlotsController's ReserveSlot status handling (PublicSlotReservationResult).
export type ReserveSlotForZipCodeResult =
  | { status: "reserved"; slotReservation: SlotReservation }
  | { status: "outOfServiceArea"; serviceArea: ServiceAreaResult }
  | { status: "noSlotsAvailable" }
  // Every candidate site failed to answer (HTTP 502), rather than answering with nothing free.
  // Distinct from noSlotsAvailable so the UI can offer a retry instead of "no appointments".
  | { status: "unavailable" };

// Matches AmsBookingsController's CreatePublicBooking request. The booking is made against the slot
// reservationUid holds, so it is required and eventTypeSlug/teamSlug/start are ignored server-side.
export interface CreatePublicBookingRequest {
  eventTypeSlug: CalEventTypeSlug;
  teamSlug: string;
  start: string;
  attendee?: Partial<BookingAttendee>;
  household?: Household;
  metadata?: Record<string, string>;
  reservationUid: string;
}

// Matches AmsIncomeGuidelinesController's IncomeGuidelineProgramDto. monthlyIncomeLimit is a Money
// server-side, which serializes as a plain number.
export interface IncomeGuidelineProgram {
  programName: string;
  monthlyIncomeLimit: number;
}

// Matches AmsIncomeGuidelinesController's IncomeGuidelinesDto. `programs` holds only the programs
// with a published limit for that year and household size, so it can be shorter than three — and
// empty, which the step renders as "not available" rather than as a list of zeroes.
export interface IncomeGuidelines {
  programYear: number;
  householdSize: number;
  programs: IncomeGuidelineProgram[];
  // AB#595 / AB#607: programs the household could be booked for but already received this program
  // year, or that an administrator switched off, each with the reason it is not offered. The step
  // shows them greyed in the same list as `programs`.
  withheld: WithheldIncomeGuideline[];
  // AB#595: every program the household could be booked for is already received; `programs` is then
  // empty and `message` is the explanation to show instead.
  noProgramRemains: boolean;
  message: string | null;
}

// Matches AmsIncomeGuidelinesController's WithheldIncomeGuidelineDto. `reason` is the customer-facing
// text (a switch message an administrator writes, or the fixed already-received wording); `reasonCode`
// says which rule withheld it. Optional, as an older API does not send it.
export interface WithheldIncomeGuideline {
  programName: string;
  reason: string;
  reasonCode?: WithheldIncomeGuidelineReasonCode;
}

// Matches WithheldIncomeGuidelineReasonCodes: switched off (AB#607) or already received (AB#595).
export type WithheldIncomeGuidelineReasonCode = "switchedOff" | "alreadyReceived";

// What getIncomeGuidelines is asked with: the household size, plus the energy answers so the server
// can quote only the programs that energy source can receive (PSE HELP only for a PSE customer).
export interface IncomeGuidelinesRequest {
  householdSize: number;
  energySource?: Household["energySource"];
  pseService?: Household["pseService"];
}

// Matches BookingsController's CreateBooking response (CreateBookingResult).
export interface CreateBookingResult {
  uid: string;
  start: string;
  confirmationCode: string;
}

export interface BookingSummary {
  uid: string;
  start: string;
}

// Matches AmsBookingsController's UpcomingHouseholdCheckRequest. The number is sent once, is never
// stored by the browser beyond the form state it was typed into, and does not come back.
export interface UpcomingHouseholdCheckRequest {
  ssn: string;
}

/**
 * Matches AmsBookingsController's UpcomingHouseholdCheckResult, and note how little is in it.
 *
 * `found: true` means MSC's duplicate rule applies: the number submitted matches one on a valid
 * upcoming appointment, of any event type, so **this booking will be blocked**. The block itself is
 * the server's — POST /api/ams/bookings answers 409 — and this check exists so the applicant finds
 * out at step 2 instead of after filling in a household questionnaire. A page cannot block anything,
 * so treat a `found: true` here as a courtesy, not as the enforcement.
 *
 * Beyond that one bit the server says almost nothing. Not when the appointment is, not where, not
 * whose name is on it, and not its confirmation code — because this check is answerable by anyone who
 * knows the applicant's Social Security number, and a page that displayed the appointment would be a
 * way to read a stranger's schedule out of one. The appointment travels one way only: down a phone
 * line, to the number already recorded on that booking.
 *
 * `phoneLast4` is the single detail that comes back, and it is here so the applicant can recognize
 * their own handset before asking us to ring it. It is absent when the booking on file carries no
 * number that can be called, which is a case the page has to handle rather than assume away.
 */
export interface UpcomingHouseholdCheckResult {
  found: boolean;
  noticeId?: string;
  phoneLast4?: string;
}

// Matches AmsBookingsController's ProgramCheckRequest (AB#590). Sent once at step 2, like the
// upcoming-household check; the energy answers are not known yet there, so they are left out and the
// server treats every program as a candidate.
export interface ProgramCheckRequest {
  ssn: string;
  energySource?: string;
  pseService?: string;
}

/**
 * Matches AmsBookingsController's ProgramCheckResult (AB#590).
 *
 * `noProgramRemains: true` means the household behind this exact SSN has already received every
 * program it could be booked for this program year, so **the booking will be refused** — the server
 * answers POST /api/ams/bookings with 409 programAlreadyReceived. Like the upcoming-household check,
 * this is the courtesy that lets the applicant find out at step 2; it is not the block.
 *
 * `applies: false` (screening off, or no usable SSN) means nothing else here means anything.
 * `receivedPrograms` holds booking program names ("Pse", "Liheap").
 */
export interface ProgramCheckResult {
  applies: boolean;
  noProgramRemains: boolean;
  receivedPrograms: string[];
  message?: string | null;
}

// Matches AmsBookingsController's cancel responses (CancelBookingResult).
export interface CancelBookingResult {
  cancelledAt: string;
}

// Matches AmsBookingsController's lookup responses (BookingDetails).
export interface BookingDetails {
  uid: string;
  status: string;
  start: string;
  end: string;
  location?: string;
  siteName?: string;
}

// ---- Out-of-band approvals ------------------------------------------------------------------
// Matches AmsBookingApprovalsController. Viewing or cancelling an appointment from the public site
// can require more than the phone number and code: those identify the booking, and the person
// holding the phone authorizes the action by answering a call from us and pressing a key.
//
// Whether it does is the server's BookingApprovalChallenge feature flag, and the page finds out from
// the response rather than by asking: RequestBookingApprovalResult below is a union, and only the
// challenge arm carries an approvalId. With the flag off the phone number and code are the whole
// authorization and the action has already happened by the time the response lands.

export type BookingApprovalAction = "ViewBooking" | "CancelBooking";

// What GET /api/ams/booking-approvals/mode reports. Read only so a page can say what is about to
// happen before the person commits to it; what actually happened is on the response to the request
// itself, and that is what behaviour branches on.
export interface BookingApprovalMode {
  phoneApprovalRequired: boolean;
  // How the phone holder approves when they must (AB#584/585): "call" rings the phone and waits for
  // a keypress, "code" sends a six-digit code the page then asks for. Reported whichever way
  // phoneApprovalRequired is set; only the copy reads it.
  approvalMode: BookingApprovalChannelMode;
}

// How an approval is answered: a call to the phone, or a one-time code sent to it.
export type BookingApprovalChannelMode = "call" | "code";

// "expired" deliberately covers more than a lapsed window — a superseded request and an
// undeliverable one report as expired too, because telling them apart would reveal facts about
// someone else's phone. See AmsBookingApprovalsController.ToWireState.
export type BookingApprovalState = "pending" | "approved" | "rejected" | "expired" | "failed";

// Exactly one of confirmationCode and ssn names the booking, and the server 400s on neither or
// both. The Social Security number is the "I have lost my confirmation number" path: it identifies
// the appointment in the code's place and authorizes nothing extra by itself — the phone number has
// to be the one recorded on that booking, and with the phone challenge on the handset still has to
// say yes. The answer shape does not vary with which one was sent.
export interface CreateBookingApprovalRequest {
  phoneNumber: string;
  confirmationCode?: string;
  ssn?: string;
  action: BookingApprovalAction;
  cancellationReason?: string;
}

// The challenge arm, returned with 202. Returned for a right code and a wrong one alike — see the
// endpoint's remarks — so an approvalId is not evidence that the code matched anything.
export interface CreateBookingApprovalResult {
  approvalId: string;
  expiresAt: string;
  pollIntervalSeconds: number;
  // "call": poll getBookingApproval until the phone answers. "code": do not poll; ask for the code that
  // was sent and verify it (verifyBookingApproval). Also returned for a decoy, so a "code" result is no
  // more evidence of a match than the approvalId is.
  mode: BookingApprovalChannelMode;
}

// The no-challenge arm, returned with 200 when BookingApprovalChallenge is off: the lookup or the
// cancellation is already done and there is nothing to poll. Exactly one field is populated, for the
// action that was asked for. Unlike the arm above, a wrong code is a 404 here rather than a result
// that never resolves. `customerNote` rides with `booking`: the latest note staff wrote for the
// attendee, as plain text (AB#624) — never an internal note.
export interface ImmediateBookingResult {
  booking?: BookingDetails;
  cancelledAt?: string;
  customerNote?: string | null;
}

export type RequestBookingApprovalResult = CreateBookingApprovalResult | ImmediateBookingResult;

// The discriminator, and the only thing the page should branch the two arms on: an approvalId is
// present precisely when there is something to poll.
export function isPhoneApprovalPending(
  result: RequestBookingApprovalResult
): result is CreateBookingApprovalResult {
  return "approvalId" in result;
}

// Code mode only: true when the page should ask for the one-time code rather than poll.
export function isCodeApproval(result: RequestBookingApprovalResult): result is CreateBookingApprovalResult {
  return isPhoneApprovalPending(result) && result.mode === "code";
}

// POST /api/ams/booking-approvals/{approvalId}/verify. `code` is exactly six digits; the server
// answers 200 BookingApprovalStatus (as a poll would), 400 CodeRejected for a wrong code, 410
// { code: "notPending" } once the approval is over, and 409 { code: "notCodeMode" } if approvals
// went back to calls. Only `code` is sent in the body; the id is in the path.
export interface VerifyBookingApprovalRequest {
  approvalId: string;
  code: string;
}

// The 400 body for a wrong one-time code. The last wrong guess fails the approval.
export interface CodeRejected {
  code: "codeRejected";
  guessesRemaining: number;
}

export function isCodeRejected(error: unknown): error is { status: 400; data: CodeRejected } {
  const failure = error as { status?: unknown; data?: { code?: unknown; guessesRemaining?: unknown } } | null | undefined;
  return failure?.status === 400 && failure?.data?.code === "codeRejected" && typeof failure.data.guessesRemaining === "number";
}

export interface BookingApprovalStatus {
  approvalId: string;
  state: BookingApprovalState;
  expiresAt: string;
  booking?: BookingDetails;
  cancelledAt?: string;
  // Released with `booking`, as on ImmediateBookingResult.
  customerNote?: string | null;
}

// Matches AmsBookingsController's ManageLinkLookupRequest. `token` is the opaque ?t= value from the
// cancellation link in a booking-details SMS; it resolves server-side to the phone number the link
// was minted for, and nothing else. The confirmation code is still required — the link is a
// convenience factor, and an authorization one.
// `approvalId` is the approved ViewBooking approval the page obtained first, sent only once the
// server has answered 403 approvalRequired (BookingApprovalChallenge on).
export interface ManageLinkLookupRequest {
  token: string;
  confirmationCode: string;
  approvalId?: string;
}

// Matches AmsBookingsController's ManageLinkCancelRequest. `approvalId` is the approved CancelBooking
// approval, as for the lookup; with the challenge on the approval itself cancels the appointment and
// this call reports when.
export interface ManageLinkCancelRequest {
  token: string;
  confirmationCode: string;
  cancellationReason?: string;
  approvalId?: string;
}
